import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert, Button } from "antd";
import { BookOpen, Film, Tv } from "lucide-react";
import { publicApi, type PublicLibraryEntry } from "../services/api";
import { RatingDisplay } from "./RatingDisplay";
import { formatDate, formatMinutes } from "../utils/period";

export const TYPE_LABELS = { book: "Książka", movie: "Film", series: "Serial" };

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <span className="brand">
      <img src="/favicon.svg" width="44" height="44" alt="" />
      {!compact && (
        <span>
          mordeczka<span className="brand-domain">.eu</span>
        </span>
      )}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
  headingLevel = 2,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <div className="section-heading">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <Heading>{title}</Heading>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function QueryError({
  retry,
  message = "Nie udało się wczytać danych.",
}: {
  retry: () => void;
  message?: string;
}) {
  return (
    <Alert
      type="error"
      showIcon
      message={message}
      action={<Button onClick={retry}>Spróbuj ponownie</Button>}
    />
  );
}

export function Cover({
  imageId,
  title,
  type,
}: {
  imageId: string | null;
  title: string;
  type: keyof typeof TYPE_LABELS;
}) {
  const [failedId, setFailedId] = useState<string | null>(null);
  const Icon = type === "book" ? BookOpen : type === "movie" ? Film : Tv;
  return (
    <div className={`editorial-cover cover-${type}`}>
      {imageId && failedId !== imageId ? (
        <img
          src={publicApi.getImageUrl(imageId)}
          alt={`Okładka: ${title}`}
          width="300"
          height="450"
          loading="lazy"
          onError={() => setFailedId(imageId)}
        />
      ) : (
        <div className="cover-placeholder">
          <Icon size={36} strokeWidth={1} />
          <span>{title}</span>
          <small>Brak okładki</small>
        </div>
      )}
    </div>
  );
}

export function WorkCard({
  entry,
  planned = false,
  compact = false,
}: {
  entry: PublicLibraryEntry;
  planned?: boolean;
  compact?: boolean;
}) {
  const detail =
    entry.work_type === "book"
      ? entry.page_count != null
        ? `${entry.page_count} stron`
        : "Brak liczby stron"
      : formatMinutes(
          entry.work_type === "series"
            ? entry.season_duration_minutes
            : entry.duration_minutes,
        );
  const venue = entry.cinema_name || entry.streaming_platform_name;
  return (
    <article className={`work-card${compact ? " work-card-compact" : ""}`}>
      <Link
        className="work-card-cover-link"
        to={`/works/${entry.work_slug}`}
        tabIndex={-1}
        aria-hidden="true"
      >
        <Cover
          imageId={entry.cover_image_id}
          title={entry.work_title}
          type={entry.work_type}
        />
      </Link>
      <div className="work-card-body">
        <div className="work-card-kicker">
          <span className={`type-label type-${entry.work_type}`}>
            {TYPE_LABELS[entry.work_type]}
          </span>
          {planned && <span className="planned-label">W planach</span>}
        </div>
        <h3>
          <Link to={`/works/${entry.work_slug}`}>{entry.work_title}</Link>
        </h3>
        {entry.work_type === "series" && entry.season_number != null && (
          <p className="season-label">Sezon {entry.season_number}</p>
        )}
        {!planned && (
          <>
            <p className="work-card-date">{formatDate(entry.finished_at)}</p>
            <div className="work-card-rating">
              <RatingDisplay value={entry.rating} />
            </div>
          </>
        )}
        <p className="work-card-meta">
          {planned ? (entry.release_year ?? "Brak roku premiery") : detail}
        </p>
        {!planned && venue && (
          <p className="work-card-meta">
            {venue}
            {entry.cinema_hall_name ? ` · ${entry.cinema_hall_name}` : ""}
          </p>
        )}
      </div>
    </article>
  );
}
